/**
 * Client-side refresh dedup wrapper.
 *
 * Multiple callers (pre-emptive interceptor + reactive 401 + provider hydrate)
 * có thể trigger refresh đồng thời — dedup vào 1 Promise duy nhất.
 *
 * Server-side `/api/auth/refresh` cũng dedup riêng (Map keyed by refresh_token)
 * — đây là layer thứ 2 phòng concurrent request.
 *
 * Doc reference: refesh-token.md §5 (race-safe pattern).
 */

import { getCookie } from 'cookies-next';
import { decodeJwtExp } from './decode-jwt';

const REFRESH_THRESHOLD_SEC = 30;

let inFlight: Promise<boolean> | null = null;

/**
 * Trigger refresh via internal API route. Dedup across all concurrent callers.
 * Returns true nếu refresh thành công, false nếu fail (caller xử lý logout).
 */
export function triggerRefresh(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      // Cleanup nhỏ trễ để Set-Cookie kịp commit vào browser store
      setTimeout(() => {
        inFlight = null;
      }, 50);
    });

  return inFlight;
}

/**
 * Pre-emptive check: refresh nếu token sắp expire (< 30s remaining).
 * Returns updated token (sau refresh) hoặc current token (nếu còn valid).
 * Returns null nếu refresh fail hoặc không có token.
 */
export async function ensureValidToken(): Promise<string | null> {
  const token = (getCookie('token') as string | undefined) ?? null;
  if (!token) return null;

  const exp = decodeJwtExp(token);
  if (!exp) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  if (exp - nowSec >= REFRESH_THRESHOLD_SEC) {
    return token; // còn dư > 30s, không cần refresh
  }

  // Sắp expire — refresh trước khi dùng
  const ok = await triggerRefresh();
  if (!ok) return null;

  return (getCookie('token') as string | undefined) ?? null;
}
